/**
 * Loads and caches interface elements for one RBAC interface name.
 */

import * as React from 'react';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { useAuthStore } from '@/stores/authStore';
import { useMode } from '@/contexts/ModeContext';
import { getInterfaceElements } from '@/services/interface-elements.service';
import { roleMode } from '@/utils/role-label-utils';
import type { InterfaceElement } from '@/types/interface-elements.types';

/** Options for {@link useInterfaceElements}. `bothModes` merges in-person + remote when the user spans both. */
export type UseInterfaceElementsOptions = {
  bothModes?: boolean;
};

/** Stable empty fallback so selectors do not allocate a new array each render. */
const EMPTY_ARRAY: InterfaceElement[] = [];

/** Persist-store key for a mode-specific slice of an interface. */
function modeCacheKey(interfaceName: string, slice: 'in_person' | 'remote'): string {
  return slice === 'in_person' ? `inperson_${interfaceName}` : `remote_${interfaceName}`;
}

/** True when the signed-in user is hybrid or holds roles in both modes. */
function userSpansBothModes(
  userMode: string | null | undefined,
  roleCodes: Array<{ code: string }>,
): boolean {
  if (userMode === 'hybrid') return true;
  const modes = new Set(roleCodes.map((r) => roleMode(r.code)).filter(Boolean));
  return modes.has('in_person') && modes.has('remote');
}

/** Deduplicate by id, drop hidden rows, sort by user/role order. */
function visibleSortedElements(raw: InterfaceElement[]): InterfaceElement[] {
  const seen = new Set<string>();
  return raw
    .filter((el) => {
      if (el.visible === false) return false;
      if (seen.has(el.id)) return false;
      seen.add(el.id);
      return true;
    })
    .sort((a, b) => {
      const orderA = a.userOrder ?? a.order ?? Infinity;
      const orderB = b.userOrder ?? b.order ?? Infinity;
      return orderA - orderB;
    });
}

/** Load RBAC layout for `interfaceName` into the persist store. */
export const useInterfaceElements = (
  interfaceName: string,
  options?: UseInterfaceElementsOptions,
) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isBootstrapping = useAuthStore((state) => state.isBootstrapping);
  const user = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.roles);
  const { mode } = useMode();

  const loadBoth = options?.bothModes === true && userSpansBothModes(user?.mode, roles);

  const keyIp = modeCacheKey(interfaceName, 'in_person');
  const keyRe = modeCacheKey(interfaceName, 'remote');

  const rawSingle = useInterfaceElementsStore(
    (state) => state.elementsByInterface[interfaceName] ?? EMPTY_ARRAY,
  );
  const rawIp = useInterfaceElementsStore(
    (state) => state.elementsByInterface[keyIp] ?? EMPTY_ARRAY,
  );
  const rawRe = useInterfaceElementsStore(
    (state) => state.elementsByInterface[keyRe] ?? EMPTY_ARRAY,
  );

  const isLoaded = useInterfaceElementsStore((state) => state.loadedInterfaces.has(interfaceName));
  const isLoading = useInterfaceElementsStore((state) =>
    state.loadingInterfaces.has(interfaceName),
  );
  const loadedIp = useInterfaceElementsStore((state) => state.loadedInterfaces.has(keyIp));
  const loadedRe = useInterfaceElementsStore((state) => state.loadedInterfaces.has(keyRe));
  const loadingIp = useInterfaceElementsStore((state) => state.loadingInterfaces.has(keyIp));
  const loadingRe = useInterfaceElementsStore((state) => state.loadingInterfaces.has(keyRe));

  const startLoading = useInterfaceElementsStore((state) => state.startLoading);
  const finishLoading = useInterfaceElementsStore((state) => state.finishLoading);
  const setInterfaceElements = useInterfaceElementsStore((state) => state.setInterfaceElements);

  React.useEffect(() => {
    if (!isAuthenticated || isBootstrapping) return;

    const { loadedInterfaces, loadingInterfaces } = useInterfaceElementsStore.getState();

    if (loadBoth) {
      const needsIp = !loadedInterfaces.has(keyIp) && !loadingInterfaces.has(keyIp);
      const needsRe = !loadedInterfaces.has(keyRe) && !loadingInterfaces.has(keyRe);
      if (!needsIp && !needsRe) return;

      if (needsIp) startLoading(keyIp);
      if (needsRe) startLoading(keyRe);

      if (needsIp) {
        getInterfaceElements(interfaceName, 'in_person')
          .then((data) => setInterfaceElements(keyIp, data))
          .catch(() => finishLoading(keyIp));
      }
      if (needsRe) {
        getInterfaceElements(interfaceName, 'remote')
          .then((data) => setInterfaceElements(keyRe, data))
          .catch(() => finishLoading(keyRe));
      }
      return;
    }

    if (mode === null) return;
    if (loadedInterfaces.has(interfaceName) || loadingInterfaces.has(interfaceName)) return;

    startLoading(interfaceName);
    getInterfaceElements(interfaceName, mode)
      .then((data) => setInterfaceElements(interfaceName, data))
      .catch(() => finishLoading(interfaceName));
  }, [
    interfaceName,
    isAuthenticated,
    isBootstrapping,
    isLoaded,
    isLoading,
    loadBoth,
    keyIp,
    keyRe,
    loadedIp,
    loadedRe,
    loadingIp,
    loadingRe,
    mode,
    startLoading,
    finishLoading,
    setInterfaceElements,
  ]);

  const rawElements = React.useMemo(
    () => (loadBoth ? [...rawIp, ...rawRe] : rawSingle),
    [loadBoth, rawIp, rawRe, rawSingle],
  );

  const elements = React.useMemo(() => visibleSortedElements(rawElements), [rawElements]);

  const refetch = React.useCallback(async () => {
    if (loadBoth) {
      const [ip, re] = await Promise.all([
        getInterfaceElements(interfaceName, 'in_person'),
        getInterfaceElements(interfaceName, 'remote'),
      ]);
      setInterfaceElements(keyIp, ip);
      setInterfaceElements(keyRe, re);
      return;
    }
    if (mode === null) return;
    try {
      const data = await getInterfaceElements(interfaceName, mode);
      setInterfaceElements(interfaceName, data);
    } catch (error) {
      console.error('Failed to refetch interface elements:', error);
    }
  }, [interfaceName, loadBoth, keyIp, keyRe, mode, setInterfaceElements]);

  return {
    elements,
    allElements: rawElements,
    isLoading: loadBoth ? !loadedIp || !loadedRe : !isLoaded,
    error: null,
    refetch,
  };
};
